/**
 * Network Page
 *
 * AWS Network Infrastructure dashboard with live data.
 * Shows detailed information about VPC, Subnets, Security Groups,
 * Internet Gateway, Route Tables, and IAM Roles.
 * 
 * Features:
 * - Real-time data from AWS APIs
 * - Security group rule management
 * - Visual network topology representation
 * - Copy resource IDs to clipboard
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useProgress } from '../contexts/ProgressContext';
import {
  Network,
  Globe,
  Shield,
  Layers,
  Route,
  Key,
  RefreshCw,
  CloudOff,
  Loader2,
  Zap,
  Trash2,
  Plus,
  ChevronRight,
  Server,
  ArrowRight,
  Clock,
  AlertTriangle,
  Activity,
} from 'lucide-react';
import {
  networkApi,
  detailedNetworkApi,
  SecurityGroupRule,
  AddSecurityGroupRuleRequest,
  gpuJobsApi,
} from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.3 },
  },
};

export default function NetworkPage() {
  const queryClient = useQueryClient();
  const [showDestroyConfirmation, setShowDestroyConfirmation] = useState(false);
  const [destroyConfirmText, setDestroyConfirmText] = useState('');
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [addRuleModal, setAddRuleModal] = useState<{ groupId: string; direction: 'inbound' | 'outbound' } | null>(null);
  const [networkToDestroy, setNetworkToDestroy] = useState<{ jobId: string; instanceId: string; vpcId: string } | null>(null);
  const [newRule, setNewRule] = useState<{
    protocol: string;
    fromPort: string;
    toPort: string;
    cidrIp: string;
    description: string;
  }>({
    protocol: 'tcp',
    fromPort: '',
    toPort: '',
    cidrIp: '0.0.0.0/0',
    description: '',
  });
  
  // Pagination state for GPU instances
  const [networkPage, setNetworkPage] = useState(1);
  const [networkPerPage, setNetworkPerPage] = useState(5);
  
  // Selection state for bulk operations
  const [selectedNetworks, setSelectedNetworks] = useState<Set<string>>(new Set());
  
  // Track toast IDs for each job to dismiss them when cleanup completes
  const activeToastIdsRef = useRef<Map<string, string>>(new Map());
  
  // Use shared progress tracking - get progress state for cleanup detection
  const { progress, startProgress, completeProgress } = useProgress();
  
  // Check if there's an active removal/cleanup in progress
  const hasActiveCleanup = Array.from(progress.values()).some(p => p.status === 'in_progress');

  // Check if there are jobs that were recently cancelled/terminated (within last 30 seconds)
  // These might still be cleaning up their networks
  const { data: gpuJobs = [] } = useQuery({
    queryKey: ['gpu-jobs'],
    queryFn: gpuJobsApi.list,
    refetchInterval: 10000,
  });

  const hasRecentCancellations = gpuJobs.some(job => {
    if (job.status !== 'cancelled' && job.status !== 'terminated') return false;
    if (!job.completedAt) return false;
    const completedTime = new Date(job.completedAt).getTime();
    const now = Date.now();
    return (now - completedTime) < 30000; // Within last 30 seconds
  });

  // Refresh more frequently if there are recent cancellations OR active cleanups
  const shouldRefreshFast = hasRecentCancellations || hasActiveCleanup;

  // Fetch detailed network status with live data
  // Auto-refresh more frequently when removal is in progress or there are recent cancellations
  const { data: networkData, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['detailed-network-status'],
    queryFn: detailedNetworkApi.getDetailedStatus,
    refetchInterval: shouldRefreshFast ? 2000 : 15000, // 2s during cleanup (faster polling), 15s normally
    staleTime: shouldRefreshFast ? 500 : 10000,
  });

  // Fetch basic network status for provisioning
  const { data: basicStatus } = useQuery({
    queryKey: ['network-status'],
    queryFn: networkApi.getStatus,
    refetchInterval: shouldRefreshFast ? 3000 : 10000,
  });

  // Force refresh network status when cleanup progress changes
  useEffect(() => {
    if (hasActiveCleanup) {
      // Immediately refetch when cleanup is in progress
      refetch();
    }
  }, [progress, hasActiveCleanup, refetch]);

  // Dismiss toasts when network cleanup completes
  useEffect(() => {
    const activeToastIds = activeToastIdsRef.current;
    const activeJobIds = new Set(progress.keys());
    
    // Clean up toast IDs for jobIds that are no longer in progress
    activeToastIds.forEach((toastId, jobId) => {
      if (!activeJobIds.has(jobId)) {
        // Progress was removed, clean up the toast ID
        toast.dismiss(toastId);
        activeToastIds.delete(jobId);
      }
    });

    // Check for completed cleanup operations
    progress.forEach((progressItem, jobId) => {
      // Check if cleanup is complete (completed or skipped status)
      if (progressItem.status === 'completed' || progressItem.status === 'skipped') {
        const toastId = activeToastIds.get(jobId);
        if (toastId) {
          // Dismiss the progress toast
          toast.dismiss(toastId);
          activeToastIds.delete(jobId);
          
          // Show success toast when cleanup is complete (only for completed, not skipped)
          if (progressItem.status === 'completed') {
            toast.success(
              <div>
                <p className="font-medium">Network cleanup complete</p>
                <p className="text-sm opacity-80 mt-1">All network resources have been removed from AWS</p>
              </div>,
              { duration: 4000 }
            );
          }
        }
      }
    });
  }, [progress]);

  // Provision network mutation
  const provisionMutation = useMutation({
    mutationFn: () => networkApi.provision(),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Network infrastructure provisioned successfully!');
      } else {
        toast.error(result.error || 'Provisioning failed');
      }
      queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
      queryClient.invalidateQueries({ queryKey: ['network-status'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to provision: ${error.message}`);
    },
  });

  // Destroy network mutation
  const destroyMutation = useMutation({
    mutationFn: () => networkApi.destroy(),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Network infrastructure destroyed successfully!');
      } else {
        toast.error(result.error || 'Destruction failed');
      }
      queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
      queryClient.invalidateQueries({ queryKey: ['network-status'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to destroy: ${error.message}`);
    },
  });

  // Add security group rule mutation
  const addRuleMutation = useMutation({
    mutationFn: ({ groupId, rule }: { groupId: string; rule: AddSecurityGroupRuleRequest }) =>
      detailedNetworkApi.addSecurityGroupRule(groupId, rule),
    onSuccess: () => {
      toast.success('Security group rule added successfully!');
      setAddRuleModal(null);
      setNewRule({ protocol: 'tcp', fromPort: '', toPort: '', cidrIp: '0.0.0.0/0', description: '' });
      refetch();
    },
    onError: (error: Error) => {
      toast.error(`Failed to add rule: ${error.message}`);
    },
  });

  // Remove security group rule mutation
  const removeRuleMutation = useMutation({
    mutationFn: ({ groupId, rule }: { groupId: string; rule: any }) =>
      detailedNetworkApi.removeSecurityGroupRule(groupId, rule),
    onSuccess: () => {
      toast.success('Security group rule removed successfully!');
      refetch();
    },
    onError: (error: Error) => {
      toast.error(`Failed to remove rule: ${error.message}`);
    },
  });

  const handleAddRule = () => {
    if (!addRuleModal) return;
    
    const port = parseInt(newRule.fromPort);
    if (isNaN(port) || port < 0 || port > 65535) {
      toast.error('Invalid port number');
      return;
    }

    addRuleMutation.mutate({
      groupId: addRuleModal.groupId,
      rule: {
        direction: addRuleModal.direction,
        protocol: newRule.protocol,
        fromPort: parseInt(newRule.fromPort),
        toPort: parseInt(newRule.toPort) || parseInt(newRule.fromPort),
        cidrIp: newRule.cidrIp,
        description: newRule.description || undefined,
      },
    });
  };

  const handleRemoveRule = (groupId: string, direction: 'inbound' | 'outbound', rule: SecurityGroupRule) => {
    if (!confirm('Are you sure you want to remove this rule?')) return;

    const firstCidr = rule.ipRanges[0]?.cidr;
    if (!firstCidr) {
      toast.error('Cannot remove rule without CIDR');
      return;
    }

    removeRuleMutation.mutate({
      groupId,
      rule: {
        direction,
        protocol: rule.protocol,
        fromPort: rule.fromPort || 0,
        toPort: rule.toPort || 0,
        cidrIp: firstCidr,
      },
    });
  };

  // Remove individual GPU job and its network - with AWS-validated progress tracking
  const removeNetworkMutation = useMutation({
    mutationFn: async ({ jobId, instanceId, vpcId }: { jobId: string; instanceId?: string; vpcId?: string }) => {
      // Get the job's network details to pass to progress tracking
      const job = gpuJobs.find(j => j.id === jobId);
      
      // Start progress with initial resources for AWS validation
      // The polling will continue even if the API call times out
      startProgress(jobId, {
        vpcId: vpcId || job?.vpcId,
        subnetId: job?.subnetId,
        securityGroupId: job?.securityGroupId,
        instanceId: instanceId || job?.instanceId,
      });
      
      try {
        return await gpuJobsApi.remove(jobId);
      } catch (error: any) {
        // If timeout, don't fail - let the AWS polling continue
        if (error.message?.includes('timeout')) {
          return { 
            deleted: true, 
            message: 'Removal initiated - polling AWS for completion' 
          };
        }
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      const { jobId } = variables;
      
      // If this is a timeout response, don't complete progress - let polling continue
      if (data.message?.includes('polling AWS')) {
        const toastId = toast(
          <div>
            <p className="font-medium">Network removal in progress</p>
            <p className="text-sm opacity-80 mt-1">Cleanup is taking longer than expected. Tracking progress via AWS...</p>
            <p className="text-xs opacity-60 mt-1">Resources are being removed gradually</p>
          </div>,
          { duration: Infinity, icon: '🔄' } // Use Infinity so it doesn't auto-dismiss
        );
        // Store toast ID to dismiss when cleanup completes
        activeToastIdsRef.current.set(jobId, toastId);
        setNetworkToDestroy(null);
        return;
      }
      
      const networkCleaned = data.message.includes('network');
      
      // DON'T complete progress here for network cleanup - let AWS polling determine when it's truly done
      // The ProgressContext will automatically complete when VPC is verified as deleted
      
      // Show an informative toast but don't mark as complete yet
      if (networkCleaned) {
        const toastId = toast(
          <div>
            <p className="font-medium">Network cleanup initiated</p>
            <p className="text-sm opacity-80 mt-1">Removing VPC, security groups, subnets...</p>
            <p className="text-xs opacity-60 mt-1">Progress tracked via AWS polling</p>
          </div>,
          { duration: Infinity, icon: '🔄' } // Use Infinity so it doesn't auto-dismiss
        );
        // Store toast ID to dismiss when cleanup completes
        activeToastIdsRef.current.set(jobId, toastId);
      } else {
        // Network preserved - can complete immediately
        completeProgress(
          jobId,
          true,
          'Instance removed, network preserved',
          true // networkPreserved = true
        );
        toast.success(
          <div>
            <p className="font-medium">Instance removed</p>
            <p className="text-sm opacity-80 mt-1">Network resources preserved for reuse</p>
          </div>
        );
      }
      setNetworkToDestroy(null);
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
      queryClient.invalidateQueries({ queryKey: ['network-status'] });
    },
    onError: (error: Error) => {
      // Don't complete progress on timeout - let AWS polling determine the outcome
      // For other errors, show error and stop progress tracking
      toast.error(
        <div>
          <p className="font-medium">Network removal failed</p>
          <p className="text-sm opacity-80 mt-1">{error.message}</p>
          <p className="text-xs opacity-70 mt-2">Check AWS console if resources remain</p>
        </div>,
        { duration: 8000 }
      );
      setNetworkToDestroy(null);
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
    },
  });

  const getProtocolLabel = (protocol: string) => {
    switch (protocol) {
      case 'tcp': return 'TCP';
      case 'udp': return 'UDP';
      case 'icmp': return 'ICMP';
      case 'all': return 'All Traffic';
      case '-1': return 'All Traffic';
      default: return protocol.toUpperCase();
    }
  };

  const getPortDisplay = (rule: SecurityGroupRule) => {
    if (rule.protocol === 'all' || rule.protocol === '-1') return 'All';
    if (rule.protocol === 'icmp') return 'N/A';
    if (rule.fromPort === rule.toPort) return String(rule.fromPort);
    return `${rule.fromPort}-${rule.toPort}`;
  };

  const isProvisioned = networkData?.isProvisioned || basicStatus?.isProvisioned;
  const isDestroying = basicStatus?.isDestroying || destroyMutation.isPending;
  
  // Check if we have ANY infrastructure to display (even if not fully provisioned)
  const hasAnyInfrastructure = networkData && (
    networkData.vpcs.length > 0 || 
    networkData.subnets.length > 0 || 
    networkData.securityGroups.length > 0 ||
    networkData.internetGateways.length > 0 ||
    networkData.routeTables.length > 0 ||
    networkData.iamRoles.length > 0
  );

  // Find platform-managed VPC (for the "isProvisioned" status)
  const platformVpc = networkData?.vpcs.find(v => v.tags?.['ManagedBy'] === 'gpu-platform');
  const platformSg = networkData?.securityGroups.find(sg => sg.tags?.['ManagedBy'] === 'gpu-platform');

  // Check if we're in a cleanup state (active cleanup or recent cancellation)
  const isCleaningUp = hasActiveCleanup || hasRecentCancellations;

  // Track what's missing for the status indicator (platform-managed components)
  // Fixed: Route tables check for association with platform VPCs (AWS auto-creates main RT without tags)
  // Fixed: IAM roles only matter if we have a VPC but no roles
  const missingComponents = networkData ? {
    vpc: !platformVpc,
    subnets: !networkData.subnets.some(s => s.tags?.['ManagedBy'] === 'gpu-platform'),
    securityGroups: !platformSg,
    internetGateway: !networkData.internetGateways.some(igw => igw.tags?.['ManagedBy'] === 'gpu-platform'),
    // Route tables: Check if ANY route table exists for platform VPCs (main RT is auto-created by AWS)
    routeTables: platformVpc ? !networkData.routeTables.some(rt => rt.vpcId === platformVpc.vpcId) : false,
    // IAM roles: Only mark as missing if we have a fully provisioned VPC but no IAM roles
    iamRoles: platformVpc && platformSg && networkData.iamRoles.length === 0,
  } : null;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-surface-100 mb-1 sm:mb-2">Network Setup</h1>
          <p className="text-sm sm:text-base text-surface-400">
            AWS Network Infrastructure
          </p>
        </div>
        <div className="flex items-center gap-3">
          {networkData?.lastUpdated && (
            <span className="text-xs text-surface-500 hidden sm:flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Updated {formatDistanceToNow(new Date(networkData.lastUpdated), { addSuffix: true })}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isLoading || isRefetching}
            className="btn-ghost"
          >
            <RefreshCw className={clsx('w-4 h-4', (isLoading || isRefetching) && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </motion.div>

      {/* Status Banner */}
      <motion.div variants={itemVariants}>
        <div className={clsx(
          'rounded-xl p-4 flex items-center justify-between',
          isProvisioned
            ? 'bg-emerald-500/10 border border-emerald-500/30'
            : hasAnyInfrastructure
              ? 'bg-yellow-500/10 border border-yellow-500/30'
              : 'bg-surface-800/50 border border-surface-700/50'
        )}>
          <div className="flex items-center gap-4">
            <div className={clsx(
              'w-12 h-12 rounded-xl flex items-center justify-center',
              isProvisioned
                ? 'bg-gradient-to-br from-emerald-500 to-teal-500'
                : hasAnyInfrastructure
                  ? 'bg-gradient-to-br from-yellow-500 to-orange-500'
                  : 'bg-gradient-to-br from-surface-700 to-surface-600'
            )}>
              <Network className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className={clsx(
                  'text-lg font-semibold',
                  isProvisioned ? 'text-emerald-300' : hasAnyInfrastructure ? 'text-yellow-300' : 'text-surface-300'
                )}>
                  {isProvisioned 
                    ? 'Infrastructure Active' 
                    : hasAnyInfrastructure 
                      ? isCleaningUp
                        ? 'Cleaning Up Infrastructure'
                        : 'Partial Infrastructure'
                      : 'Infrastructure Not Deployed'}
                </h3>
                {(isProvisioned || hasAnyInfrastructure) && (
                  <span className={clsx(
                    'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs',
                    isProvisioned 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : isCleaningUp
                        ? 'bg-orange-500/20 text-orange-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                  )}>
                    {isCleaningUp ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Removing
                      </>
                    ) : (
                      <>
                        <Activity className="w-3 h-3" />
                        Live
                      </>
                    )}
                  </span>
                )}
              </div>
              <p className="text-sm text-surface-400">
                {isProvisioned 
                  ? `Region: ${networkData?.region || basicStatus?.region} • Account: ${networkData?.accountId || basicStatus?.accountId}`
                  : hasAnyInfrastructure
                    ? isCleaningUp
                      ? `Region: ${networkData?.region} • Removing network resources (VPCs: ${networkData?.vpcs.length || 0}, Subnets: ${networkData?.subnets.length || 0}, SGs: ${networkData?.securityGroups.filter(sg => sg.groupName !== 'default').length || 0})...`
                      : `Region: ${networkData?.region} • Some components missing (${[
                          missingComponents?.vpc && 'VPC',
                          missingComponents?.subnets && 'Subnets',
                          missingComponents?.securityGroups && 'Security Groups',
                          missingComponents?.internetGateway && 'Internet Gateway',
                          missingComponents?.routeTables && 'Route Tables',
                          missingComponents?.iamRoles && 'IAM Roles',
                        ].filter(Boolean).join(', ') || 'unknown'})`
                    : 'Deploy your AWS network infrastructure to get started'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Show Destroy button when there's any infrastructure (partial OR full) */}
            {(isProvisioned || hasAnyInfrastructure) && (
              <button
                onClick={() => setShowDestroyConfirmation(true)}
                disabled={isDestroying}
                className="btn-ghost text-red-400 hover:bg-red-500/10"
              >
                {isDestroying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Destroying...
                  </>
                ) : (
                  <>
                    <CloudOff className="w-4 h-4" />
                    Destroy All
                  </>
                )}
              </button>
            )}
            {/* Show Deploy/Complete Setup button when not fully provisioned */}
            {!isProvisioned && (
              <button
                onClick={() => provisionMutation.mutate()}
                disabled={provisionMutation.isPending}
                className="btn-primary"
              >
                {provisionMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deploying...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    {hasAnyInfrastructure ? 'Complete Setup' : 'Deploy Infrastructure'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Loading State */}
      {isLoading && !networkData && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gpu-400" />
        </div>
      )}

      {/* Error State */}
      {networkData?.error && (
        <motion.div variants={itemVariants}>
          <div className="rounded-xl p-4 bg-red-500/10 border border-red-500/30 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div>
              <h4 className="font-medium text-red-300">Error Loading Network Data</h4>
              <p className="text-sm text-red-400/80">{networkData.error}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Instance-Based Network View */}
      {hasAnyInfrastructure && networkData && (
        <motion.div variants={itemVariants} className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Network className="w-5 h-5 text-gpu-400" />
              <h3 className="text-lg font-semibold text-surface-100">GPU Instance Networks</h3>
            </div>
            
            {gpuJobs.length > 0 && (
              <div className="flex items-center gap-3">
                {/* Bulk Actions */}
                {selectedNetworks.size > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-surface-400">
                      {selectedNetworks.size} selected
                    </span>
                    <button
                      onClick={() => {
                        // Filter out jobs that are not in destroyed state
                        const destroyableJobs = gpuJobs.filter(job => 
                          selectedNetworks.has(job.id) && 
                          (job.status === 'stopped' || job.status === 'terminated' || job.status === 'cancelled')
                        );
                        
                        if (destroyableJobs.length === 0) {
                          toast.error('No destroyable networks selected. Only stopped/terminated instances can be destroyed.');
                          return;
                        }
                        
                        if (!confirm(`Are you sure you want to destroy ${destroyableJobs.length} network(s)?`)) {
                          return;
                        }
                        
                        // Destroy each selected network
                        destroyableJobs.forEach(job => {
                          removeNetworkMutation.mutate({
                            jobId: job.id,
                            instanceId: job.instanceId || job.id,
                            vpcId: job.vpcId,
                          });
                        });
                        setSelectedNetworks(new Set());
                      }}
                      disabled={removeNetworkMutation.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Selected
                    </button>
                    <button
                      onClick={() => setSelectedNetworks(new Set())}
                      className="text-sm text-surface-400 hover:text-surface-300"
                    >
                      Clear
                    </button>
                  </div>
                )}
                
                {/* Pagination Size Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-surface-400">Per page:</span>
                  <select
                    value={networkPerPage}
                    onChange={(e) => {
                      setNetworkPerPage(Number(e.target.value));
                      setNetworkPage(1); // Reset to first page
                    }}
                    className="px-2 py-1 rounded-lg bg-surface-800 border border-surface-700 text-surface-300 text-sm focus:border-gpu-500 focus:ring-1 focus:ring-gpu-500/50 outline-none"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          
          {gpuJobs.length > 0 ? (
            <>
            {/* Select All Checkbox */}
            <div className="flex items-center gap-2 px-4 py-2 mb-2 bg-surface-800/30 rounded-lg">
              <input
                type="checkbox"
                checked={gpuJobs.length > 0 && selectedNetworks.size === gpuJobs.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedNetworks(new Set(gpuJobs.map(job => job.id)));
                  } else {
                    setSelectedNetworks(new Set());
                  }
                }}
                className="w-4 h-4 rounded border-surface-600 text-gpu-500 focus:ring-gpu-500/50 focus:ring-offset-0 bg-surface-800"
              />
              <span className="text-sm text-surface-400">
                Select all ({gpuJobs.length} networks)
              </span>
            </div>
            
            <div className="space-y-2">
              {/* Group instances by VPC to show shared vs dedicated networks */}
              {(() => {
                // Group jobs by VPC ID
                const jobsByVpc = gpuJobs.reduce((acc, job) => {
                  const vpcId = job.vpcId || 'no-vpc';
                  if (!acc[vpcId]) acc[vpcId] = [];
                  acc[vpcId].push(job);
                  return acc;
                }, {} as Record<string, typeof gpuJobs>);

                // Pagination logic
                const vpcEntries = Object.entries(jobsByVpc);
                const totalPages = Math.ceil(vpcEntries.length / networkPerPage);
                const startIndex = (networkPage - 1) * networkPerPage;
                const endIndex = startIndex + networkPerPage;
                const paginatedEntries = vpcEntries.slice(startIndex, endIndex);

                return (
                  <>
                    {paginatedEntries.map(([vpcId, jobs]) => {
                  const isSharedNetwork = jobs.length > 1;
                  const vpc = networkData.vpcs.find(v => v.vpcId === vpcId);
                  
                  if (vpcId === 'no-vpc') {
                    // Instances without VPC
                    return jobs.map((job) => (
                      <div key={job.id} className="rounded-lg border border-surface-700/50 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 bg-surface-800/50">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedNetworks.has(job.id)}
                              onChange={(e) => {
                                const newSelection = new Set(selectedNetworks);
                                if (e.target.checked) {
                                  newSelection.add(job.id);
                                } else {
                                  newSelection.delete(job.id);
                                }
                                setSelectedNetworks(newSelection);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 rounded border-surface-600 text-gpu-500 focus:ring-gpu-500/50 focus:ring-offset-0 bg-surface-800"
                            />
                            <Server className="w-4 h-4 text-surface-500" />
                            <span className="font-medium text-surface-100">
                              {job.instanceId || `GPU Instance ${job.id.slice(0, 8)}`}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-surface-600/50 text-surface-400">
                              No Network
                            </span>
                          </div>
                        </div>
                      </div>
                    ));
                  }

                  if (isSharedNetwork) {
                    // Multiple instances sharing one network
                    const isExpanded = selectedInstanceId === `vpc-${vpcId}`;
                    const allJobsInNetworkSelected = jobs.every(job => selectedNetworks.has(job.id));
                    const someJobsInNetworkSelected = jobs.some(job => selectedNetworks.has(job.id));
                    
                    return (
                      <div key={vpcId} className="rounded-lg border border-surface-700/50 overflow-hidden">
                        {/* Shared Network Header */}
                        <div 
                          className={clsx(
                            'flex items-center justify-between px-4 py-3 cursor-pointer transition-colors',
                            isExpanded ? 'bg-gpu-500/10' : 'bg-surface-800/50 hover:bg-surface-800/80'
                          )}
                          onClick={() => setSelectedInstanceId(isExpanded ? null : `vpc-${vpcId}`)}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={allJobsInNetworkSelected}
                              ref={(input) => {
                                if (input) {
                                  input.indeterminate = someJobsInNetworkSelected && !allJobsInNetworkSelected;
                                }
                              }}
                              onChange={(e) => {
                                const newSelection = new Set(selectedNetworks);
                                jobs.forEach(job => {
                                  if (e.target.checked) {
                                    newSelection.add(job.id);
                                  } else {
                                    newSelection.delete(job.id);
                                  }
                                });
                                setSelectedNetworks(newSelection);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 rounded border-surface-600 text-gpu-500 focus:ring-gpu-500/50 focus:ring-offset-0 bg-surface-800"
                            />
                            <Globe className="w-4 h-4 text-blue-400" />
                            <span className="font-medium text-surface-100">
                              {jobs.length} Instances (Shared Network)
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">
                              Shared VPC
                            </span>
                          </div>
                          <ChevronRight className={clsx(
                            'w-5 h-5 text-surface-400 transition-transform duration-200',
                            isExpanded && 'rotate-90'
                          )} />
                        </div>

                        {/* Expanded Content */}
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="border-t border-surface-700/50 bg-surface-900/30"
                          >
                            <div className="p-4 space-y-4">
                              {/* Instances List */}
                              <div className="p-3 rounded-lg bg-surface-800/50">
                                <div className="flex items-center gap-2 mb-2">
                                  <Server className="w-4 h-4 text-gpu-400" />
                                  <span className="text-sm font-medium text-surface-200">Instances</span>
                                </div>
                                <div className="space-y-2">
                                  {jobs.map((job) => (
                                    <div key={job.id} className="flex items-center justify-between p-2 rounded bg-surface-900/50">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={selectedNetworks.has(job.id)}
                                          onChange={(e) => {
                                            const newSelection = new Set(selectedNetworks);
                                            if (e.target.checked) {
                                              newSelection.add(job.id);
                                            } else {
                                              newSelection.delete(job.id);
                                            }
                                            setSelectedNetworks(newSelection);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="w-3.5 h-3.5 rounded border-surface-600 text-gpu-500 focus:ring-gpu-500/50 focus:ring-offset-0 bg-surface-800"
                                        />
                                        <span className="font-mono text-xs text-surface-300">{job.instanceId || job.id.slice(0, 8)}</span>
                                        <span className="text-xs text-surface-500">{job.instanceType}</span>
                                      </div>
                                      <span className={clsx(
                                        'text-xs px-2 py-0.5 rounded-full',
                                        job.status === 'running' ? 'bg-emerald-500/20 text-emerald-300' :
                                        job.status === 'pending' || job.status === 'provisioning' ? 'bg-yellow-500/20 text-yellow-300' :
                                        job.status === 'stopping' ? 'bg-yellow-500/20 text-yellow-300' :
                                        job.status === 'stopped' ? 'bg-orange-500/20 text-orange-300' :
                                        job.status === 'cancelled' ? 'bg-gray-500/20 text-gray-300' :
                                        'bg-surface-600/50 text-surface-400'
                                      )}>
                                        {job.status}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* VPC */}
                              {vpc && (
                                <div className="p-3 rounded-lg bg-surface-800/50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Layers className="w-4 h-4 text-blue-400" />
                                    <span className="text-sm font-medium text-surface-200">VPC</span>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                    <div>
                                      <span className="text-surface-500">ID</span>
                                      <p className="font-mono text-surface-300">{vpc.vpcId}</p>
                                    </div>
                                    <div>
                                      <span className="text-surface-500">CIDR</span>
                                      <p className="font-mono text-surface-300">{vpc.cidrBlock}</p>
                                    </div>
                                    <div>
                                      <span className="text-surface-500">State</span>
                                      <p className={vpc.state === 'available' ? 'text-emerald-400' : 'text-surface-400'}>{vpc.state}</p>
                                    </div>
                                    <div>
                                      <span className="text-surface-500">Tenancy</span>
                                      <p className="text-surface-300">{vpc.instanceTenancy || 'default'}</p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Subnets */}
                              {(() => {
                                const subnets = networkData.subnets.filter(s => s.vpcId === vpcId);
                                return subnets.length > 0 && (
                                  <div className="p-3 rounded-lg bg-surface-800/50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Layers className="w-4 h-4 text-teal-400" />
                                      <span className="text-sm font-medium text-surface-200">Subnets</span>
                                    </div>
                                    {subnets.map((subnet) => (
                                      <div key={subnet.subnetId} className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-2">
                                        <div>
                                          <span className="text-surface-500">ID</span>
                                          <p className="font-mono text-surface-300">{subnet.subnetId}</p>
                                        </div>
                                        <div>
                                          <span className="text-surface-500">CIDR</span>
                                          <p className="font-mono text-surface-300">{subnet.cidrBlock}</p>
                                        </div>
                                        <div>
                                          <span className="text-surface-500">AZ</span>
                                          <p className="text-surface-300">{subnet.availabilityZone}</p>
                                        </div>
                                        <div>
                                          <span className="text-surface-500">State</span>
                                          <p className={subnet.state === 'available' ? 'text-emerald-400' : 'text-surface-400'}>{subnet.state}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}

                              {/* Security Groups */}
                              {(() => {
                                const sgs = networkData.securityGroups.filter(sg => sg.vpcId === vpcId);
                                return sgs.length > 0 && (
                                  <div className="p-3 rounded-lg bg-surface-800/50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Shield className="w-4 h-4 text-orange-400" />
                                      <span className="text-sm font-medium text-surface-200">Security Groups</span>
                                    </div>
                                    {sgs.map((sg) => (
                                      <div key={sg.groupId} className="mb-3">
                                        <div className="grid grid-cols-2 gap-3 text-xs mb-2">
                                          <div>
                                            <span className="text-surface-500">ID</span>
                                            <p className="font-mono text-surface-300">{sg.groupId}</p>
                                          </div>
                                          <div>
                                            <span className="text-surface-500">Name</span>
                                            <p className="font-mono text-surface-300">{sg.groupName}</p>
                                          </div>
                                        </div>
                                        
                                        {/* Inbound Rules */}
                                        <div className="mb-2">
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs text-emerald-400 font-medium">Inbound</span>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setAddRuleModal({ groupId: sg.groupId, direction: 'inbound' });
                                              }}
                                              className="text-[10px] text-gpu-400 hover:text-gpu-300 flex items-center gap-1"
                                            >
                                              <Plus className="w-3 h-3" />
                                              Add
                                            </button>
                                          </div>
                                          <div className="space-y-1">
                                            {sg.inboundRules.length === 0 ? (
                                              <span className="text-xs text-surface-500 italic">No rules</span>
                                            ) : (
                                              sg.inboundRules.map((rule, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-xs p-1.5 rounded bg-surface-900/50 group">
                                                  <div className="flex items-center gap-2">
                                                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono text-[10px]">
                                                      {getProtocolLabel(rule.protocol)}
                                                    </span>
                                                    <span className="text-surface-400">Port {getPortDisplay(rule)}</span>
                                                    <span className="text-surface-500">← {rule.ipRanges[0]?.cidr || 'SG ref'}</span>
                                                  </div>
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRemoveRule(sg.groupId, 'inbound', rule);
                                                    }}
                                                    className="p-1 rounded hover:bg-red-500/20 text-surface-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              ))
                                            )}
                                          </div>
                                        </div>

                                        {/* Outbound Rules */}
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs text-blue-400 font-medium">Outbound</span>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setAddRuleModal({ groupId: sg.groupId, direction: 'outbound' });
                                              }}
                                              className="text-[10px] text-gpu-400 hover:text-gpu-300 flex items-center gap-1"
                                            >
                                              <Plus className="w-3 h-3" />
                                              Add
                                            </button>
                                          </div>
                                          <div className="space-y-1">
                                            {sg.outboundRules.length === 0 ? (
                                              <span className="text-xs text-surface-500 italic">No rules</span>
                                            ) : (
                                              sg.outboundRules.map((rule, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-xs p-1.5 rounded bg-surface-900/50 group">
                                                  <div className="flex items-center gap-2">
                                                    <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-mono text-[10px]">
                                                      {getProtocolLabel(rule.protocol)}
                                                    </span>
                                                    <span className="text-surface-400">Port {getPortDisplay(rule)}</span>
                                                    <span className="text-surface-500">→ {rule.ipRanges[0]?.cidr || 'SG ref'}</span>
                                                  </div>
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRemoveRule(sg.groupId, 'outbound', rule);
                                                    }}
                                                    className="p-1 rounded hover:bg-red-500/20 text-surface-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              ))
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}

                              {/* Route Tables */}
                              {(() => {
                                const rts = networkData.routeTables.filter(rt => rt.vpcId === vpcId);
                                return rts.length > 0 && (
                                  <div className="p-3 rounded-lg bg-surface-800/50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Route className="w-4 h-4 text-purple-400" />
                                      <span className="text-sm font-medium text-surface-200">Route Tables</span>
                                    </div>
                                    <div className="text-xs">
                                      <span className="text-surface-500">ID: </span>
                                      <span className="font-mono text-surface-300">{rts[0].routeTableId}</span>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Danger Zone - Destroy All Instances in Shared Network */}
                              <div className="pt-3 mt-3 border-t border-red-500/20">
                                <p className="text-xs text-surface-500 mb-2">
                                  Destroying this network will remove all {jobs.length} instances and associated resources.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {jobs.map((job) => (
                                    <button
                                      key={job.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setNetworkToDestroy({
                                          jobId: job.id,
                                          instanceId: job.instanceId || job.id,
                                          vpcId: vpcId
                                        });
                                      }}
                                      disabled={removeNetworkMutation.isPending}
                                      className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors text-xs"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                      {job.instanceId || job.id.slice(0, 8)}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    );
                  } else {
                    // Single instance with dedicated network
                    const job = jobs[0];
                    const isExpanded = selectedInstanceId === (job.instanceId || job.id);
                    
                    return (
                      <div key={job.id} className="rounded-lg border border-surface-700/50 overflow-hidden">
                        {/* Instance Header */}
                        <div 
                          className={clsx(
                            'flex items-center justify-between px-4 py-3 cursor-pointer transition-colors',
                            isExpanded ? 'bg-gpu-500/10' : 'bg-surface-800/50 hover:bg-surface-800/80'
                          )}
                          onClick={() => setSelectedInstanceId(isExpanded ? null : (job.instanceId || job.id))}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedNetworks.has(job.id)}
                              onChange={(e) => {
                                const newSelection = new Set(selectedNetworks);
                                if (e.target.checked) {
                                  newSelection.add(job.id);
                                } else {
                                  newSelection.delete(job.id);
                                }
                                setSelectedNetworks(newSelection);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 rounded border-surface-600 text-gpu-500 focus:ring-gpu-500/50 focus:ring-offset-0 bg-surface-800"
                            />
                            <Server className="w-4 h-4 text-gpu-400" />
                            <span className="font-medium text-surface-100">
                              {job.instanceId || `GPU Instance ${job.id.slice(0, 8)}`}
                            </span>
                            <span className={clsx(
                              'text-xs px-2 py-0.5 rounded-full',
                              job.status === 'running' ? 'bg-emerald-500/20 text-emerald-300' :
                              job.status === 'pending' || job.status === 'provisioning' ? 'bg-yellow-500/20 text-yellow-300' :
                              job.status === 'stopping' ? 'bg-yellow-500/20 text-yellow-300' :
                              job.status === 'stopped' ? 'bg-orange-500/20 text-orange-300' :
                              job.status === 'cancelled' ? 'bg-gray-500/20 text-gray-300' :
                              'bg-surface-600/50 text-surface-400'
                            )}>
                              {job.status}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
                              Dedicated
                            </span>
                          </div>
                          <ChevronRight className={clsx(
                            'w-5 h-5 text-surface-400 transition-transform duration-200',
                            isExpanded && 'rotate-90'
                          )} />
                        </div>

                        {/* Expanded Network Details */}
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="border-t border-surface-700/50 bg-surface-900/30"
                          >
                            <div className="p-4 space-y-4">
                              {/* VPC */}
                              {vpc && (
                                <div className="p-3 rounded-lg bg-surface-800/50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Layers className="w-4 h-4 text-blue-400" />
                                    <span className="text-sm font-medium text-surface-200">VPC</span>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                    <div>
                                      <span className="text-surface-500">ID</span>
                                      <p className="font-mono text-surface-300">{vpc.vpcId}</p>
                                    </div>
                                    <div>
                                      <span className="text-surface-500">CIDR</span>
                                      <p className="font-mono text-surface-300">{vpc.cidrBlock}</p>
                                    </div>
                                    <div>
                                      <span className="text-surface-500">State</span>
                                      <p className={vpc.state === 'available' ? 'text-emerald-400' : 'text-surface-400'}>{vpc.state}</p>
                                    </div>
                                    <div>
                                      <span className="text-surface-500">Gateway</span>
                                      <p className="font-mono text-surface-300">
                                        {networkData.internetGateways.find(igw => igw.attachments.some(a => a.vpcId === vpcId))?.internetGatewayId || 'N/A'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Subnet */}
                              {(() => {
                                const subnets = networkData.subnets.filter(s => s.vpcId === vpcId);
                                return subnets.length > 0 && (
                                  <div className="p-3 rounded-lg bg-surface-800/50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Layers className="w-4 h-4 text-teal-400" />
                                      <span className="text-sm font-medium text-surface-200">Subnet</span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                      <div>
                                        <span className="text-surface-500">ID</span>
                                        <p className="font-mono text-surface-300">{subnets[0].subnetId}</p>
                                      </div>
                                      <div>
                                        <span className="text-surface-500">CIDR</span>
                                        <p className="font-mono text-surface-300">{subnets[0].cidrBlock}</p>
                                      </div>
                                      <div>
                                        <span className="text-surface-500">AZ</span>
                                        <p className="text-surface-300">{subnets[0].availabilityZone}</p>
                                      </div>
                                      <div>
                                        <span className="text-surface-500">State</span>
                                        <p className={subnets[0].state === 'available' ? 'text-emerald-400' : 'text-surface-400'}>{subnets[0].state}</p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Security Group */}
                              {(() => {
                                const sg = networkData.securityGroups.find(sg => 
                                  job.securityGroupId && sg.groupId === job.securityGroupId
                                ) || networkData.securityGroups.find(sg => sg.vpcId === vpcId);
                                
                                return sg && (
                                  <div className="p-3 rounded-lg bg-surface-800/50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Shield className="w-4 h-4 text-orange-400" />
                                      <span className="text-sm font-medium text-surface-200">Security Group</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                                      <div>
                                        <span className="text-surface-500">ID</span>
                                        <p className="font-mono text-surface-300">{sg.groupId}</p>
                                      </div>
                                      <div>
                                        <span className="text-surface-500">Name</span>
                                        <p className="font-mono text-surface-300">{sg.groupName}</p>
                                      </div>
                                    </div>

                                    {/* Inbound Rules */}
                                    <div className="mb-3">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs text-emerald-400 font-medium">Inbound Rules</span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setAddRuleModal({ groupId: sg.groupId, direction: 'inbound' });
                                          }}
                                          className="text-[10px] text-gpu-400 hover:text-gpu-300 flex items-center gap-1"
                                        >
                                          <Plus className="w-3 h-3" />
                                          Add
                                        </button>
                                      </div>
                                      <div className="space-y-1">
                                        {sg.inboundRules.length === 0 ? (
                                          <span className="text-xs text-surface-500 italic">No inbound rules</span>
                                        ) : (
                                          sg.inboundRules.map((rule, idx) => (
                                            <div key={idx} className="flex items-center justify-between text-xs p-1.5 rounded bg-surface-900/50 group">
                                              <div className="flex items-center gap-2">
                                                <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono text-[10px]">
                                                  {getProtocolLabel(rule.protocol)}
                                                </span>
                                                <span className="text-surface-400">Port {getPortDisplay(rule)}</span>
                                                <span className="text-surface-500">←</span>
                                                <span className="font-mono text-surface-300">{rule.ipRanges[0]?.cidr || 'SG ref'}</span>
                                              </div>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleRemoveRule(sg.groupId, 'inbound', rule);
                                                }}
                                                className="p-1 rounded hover:bg-red-500/20 text-surface-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </button>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </div>

                                    {/* Outbound Rules */}
                                    <div>
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs text-blue-400 font-medium">Outbound Rules</span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setAddRuleModal({ groupId: sg.groupId, direction: 'outbound' });
                                          }}
                                          className="text-[10px] text-gpu-400 hover:text-gpu-300 flex items-center gap-1"
                                        >
                                          <Plus className="w-3 h-3" />
                                          Add
                                        </button>
                                      </div>
                                      <div className="space-y-1">
                                        {sg.outboundRules.length === 0 ? (
                                          <span className="text-xs text-surface-500 italic">No outbound rules</span>
                                        ) : (
                                          sg.outboundRules.map((rule, idx) => (
                                            <div key={idx} className="flex items-center justify-between text-xs p-1.5 rounded bg-surface-900/50 group">
                                              <div className="flex items-center gap-2">
                                                <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-mono text-[10px]">
                                                  {getProtocolLabel(rule.protocol)}
                                                </span>
                                                <span className="text-surface-400">Port {getPortDisplay(rule)}</span>
                                                <span className="text-surface-500">→</span>
                                                <span className="font-mono text-surface-300">{rule.ipRanges[0]?.cidr || 'SG ref'}</span>
                                              </div>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleRemoveRule(sg.groupId, 'outbound', rule);
                                                }}
                                                className="p-1 rounded hover:bg-red-500/20 text-surface-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </button>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Route Table */}
                              {(() => {
                                const rts = networkData.routeTables.filter(rt => rt.vpcId === vpcId);
                                return rts.length > 0 && (
                                  <div className="p-3 rounded-lg bg-surface-800/50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Route className="w-4 h-4 text-purple-400" />
                                      <span className="text-sm font-medium text-surface-200">Route Table</span>
                                    </div>
                                    <div className="text-xs mb-2">
                                      <span className="text-surface-500">ID: </span>
                                      <span className="font-mono text-surface-300">{rts[0].routeTableId}</span>
                                    </div>
                                    <div className="space-y-1">
                                      {rts[0].routes.map((route, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-xs p-1.5 rounded bg-surface-900/50">
                                          <code className="text-surface-300">{route.destinationCidrBlock}</code>
                                          <ArrowRight className="w-3 h-3 text-surface-500" />
                                          <span className={route.gatewayId?.includes('igw') ? 'text-blue-400' : 'text-surface-300'}>
                                            {route.gatewayId || 'local'}
                                          </span>
                                          <span className={clsx(
                                            'px-1 py-0.5 rounded text-[10px]',
                                            route.state === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-yellow-500/20 text-yellow-300'
                                          )}>
                                            {route.state}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Instance Info */}
                              <div className="p-3 rounded-lg bg-gpu-500/10 border border-gpu-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                  <Server className="w-4 h-4 text-gpu-400" />
                                  <span className="text-sm font-medium text-surface-200">Instance Info</span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                  <div>
                                    <span className="text-surface-500">Instance ID</span>
                                    <p className="font-mono text-surface-300">{job.instanceId || 'Pending'}</p>
                                  </div>
                                  <div>
                                    <span className="text-surface-500">Type</span>
                                    <p className="font-mono text-surface-300">{job.instanceType}</p>
                                  </div>
                                  {job.publicIpAddress && (
                                    <div>
                                      <span className="text-surface-500">Public IP</span>
                                      <p className="font-mono text-gpu-400">{job.publicIpAddress}</p>
                                    </div>
                                  )}
                                  {job.privateIpAddress && (
                                    <div>
                                      <span className="text-surface-500">Private IP</span>
                                      <p className="font-mono text-surface-300">{job.privateIpAddress}</p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Danger Zone - Destroy Network */}
                              <div className="pt-3 mt-3 border-t border-red-500/20">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setNetworkToDestroy({
                                      jobId: job.id,
                                      instanceId: job.instanceId || job.id,
                                      vpcId: vpcId
                                    });
                                  }}
                                  disabled={removeNetworkMutation.isPending}
                                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors text-sm"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Destroy This Network
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    );
                  }
                })}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-4 mt-4 border-t border-surface-700">
                        <div className="text-sm text-surface-400">
                          Showing {startIndex + 1}-{Math.min(endIndex, vpcEntries.length)} of {vpcEntries.length} networks
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setNetworkPage(Math.max(1, networkPage - 1))}
                            disabled={networkPage === 1}
                            className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronRight className="w-4 h-4 rotate-180" />
                          </button>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                              // Show first, last, current, and pages around current
                              if (
                                page === 1 ||
                                page === totalPages ||
                                (page >= networkPage - 1 && page <= networkPage + 1)
                              ) {
                                return (
                                  <button
                                    key={page}
                                    onClick={() => setNetworkPage(page)}
                                    className={clsx(
                                      'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                                      page === networkPage
                                        ? 'bg-gpu-500 text-white'
                                        : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                                    )}
                                  >
                                    {page}
                                  </button>
                                );
                              } else if (
                                page === networkPage - 2 ||
                                page === networkPage + 2
                              ) {
                                return <span key={page} className="text-surface-500">...</span>;
                              }
                              return null;
                            })}
                          </div>
                          <button
                            onClick={() => setNetworkPage(Math.min(totalPages, networkPage + 1))}
                            disabled={networkPage === totalPages}
                            className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            </>
          ) : (
            <div className="text-center py-12 text-surface-400">
              <Server className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No GPU instances deployed</p>
              <p className="text-sm text-surface-500 mt-1">Create GPU instances to view their network infrastructure</p>
            </div>
          )}
        </motion.div>
      )}

      {/* Not Provisioned State - Only show when there's NO infrastructure */}
      {!isProvisioned && !hasAnyInfrastructure && !isLoading && (
        <motion.div variants={itemVariants} className="card">
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-gpu-500/20 to-violet-500/20 flex items-center justify-center mb-6">
              <Network className="w-10 h-10 text-gpu-400" />
            </div>
            <h3 className="text-2xl font-bold text-surface-100 mb-3">No Infrastructure Deployed</h3>
            <p className="text-surface-400 max-w-md mx-auto mb-6">
              Deploy your AWS network infrastructure to create a VPC, subnets, security groups, 
              and IAM roles required for running GPU instances.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-lg mx-auto mb-8">
              {[
                { icon: Globe, label: 'VPC', desc: '10.0.0.0/16' },
                { icon: Layers, label: 'Public Subnet', desc: 'Auto-assign IP' },
                { icon: Shield, label: 'Security Group', desc: 'SSH + App ports' },
                { icon: Route, label: 'Route Table', desc: 'Internet access' },
                { icon: Server, label: 'Internet Gateway', desc: 'Public routing' },
                { icon: Key, label: 'IAM Roles', desc: 'EC2 permissions' },
              ].map((item, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-surface-800/50 text-left">
                  <item.icon className="w-5 h-5 text-gpu-400 mb-2" />
                  <div className="text-sm font-medium text-surface-200">{item.label}</div>
                  <div className="text-xs text-surface-500">{item.desc}</div>
                </div>
              ))}
            </div>
            <button
              onClick={() => provisionMutation.mutate()}
              disabled={provisionMutation.isPending}
              className="btn-primary text-lg px-8 py-3"
            >
              {provisionMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Deploying Infrastructure...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  Deploy Infrastructure
                </>
              )}
            </button>
          </div>
        </motion.div>
      )}

      {/* Destroy Network Confirmation Modal */}
      <AnimatePresence>
        {networkToDestroy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setNetworkToDestroy(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface-900 border border-red-500/30 rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-400">Destroy Network</h3>
                  <p className="text-sm text-surface-400">This action cannot be undone</p>
                </div>
              </div>

              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                <p className="text-sm text-surface-200 mb-3">
                  This will permanently destroy the following resources:
                </p>
                <ul className="text-sm text-surface-300 space-y-1.5">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    Instance: <code className="text-red-300 font-mono text-xs">{networkToDestroy.instanceId}</code>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    VPC: <code className="text-red-300 font-mono text-xs">{networkToDestroy.vpcId}</code>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    Associated subnets, route tables, and security groups
                  </li>
                </ul>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setNetworkToDestroy(null)}
                  className="btn-ghost"
                >
                  Cancel
                </button>
                <button
                  onClick={() => removeNetworkMutation.mutate({
                    jobId: networkToDestroy.jobId,
                    instanceId: networkToDestroy.instanceId,
                    vpcId: networkToDestroy.vpcId,
                  })}
                  disabled={removeNetworkMutation.isPending}
                  className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  {removeNetworkMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Destroying...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Destroy Network
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Rule Modal */}
      <AnimatePresence>
        {addRuleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setAddRuleModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface-900 border border-surface-700 rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-surface-100 mb-4">
                Add {addRuleModal.direction === 'inbound' ? 'Inbound' : 'Outbound'} Rule
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="label">Protocol</label>
                  <select
                    value={newRule.protocol}
                    onChange={(e) => setNewRule({ ...newRule, protocol: e.target.value })}
                    className="input"
                  >
                    <option value="tcp">TCP</option>
                    <option value="udp">UDP</option>
                    <option value="icmp">ICMP</option>
                    <option value="all">All Traffic</option>
                  </select>
                </div>
                {newRule.protocol !== 'all' && newRule.protocol !== 'icmp' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">From Port</label>
                      <input
                        type="number"
                        value={newRule.fromPort}
                        onChange={(e) => setNewRule({ ...newRule, fromPort: e.target.value })}
                        placeholder="e.g. 22"
                        className="input"
                        min="0"
                        max="65535"
                      />
                    </div>
                    <div>
                      <label className="label">To Port</label>
                      <input
                        type="number"
                        value={newRule.toPort}
                        onChange={(e) => setNewRule({ ...newRule, toPort: e.target.value })}
                        placeholder="e.g. 22"
                        className="input"
                        min="0"
                        max="65535"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="label">Source/Destination CIDR</label>
                  <input
                    type="text"
                    value={newRule.cidrIp}
                    onChange={(e) => setNewRule({ ...newRule, cidrIp: e.target.value })}
                    placeholder="0.0.0.0/0"
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Description (optional)</label>
                  <input
                    type="text"
                    value={newRule.description}
                    onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                    placeholder="Rule description"
                    className="input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setAddRuleModal(null)} className="btn-ghost">
                  Cancel
                </button>
                <button
                  onClick={handleAddRule}
                  disabled={addRuleMutation.isPending}
                  className="btn-primary"
                >
                  {addRuleMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Add Rule
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Destroy Confirmation Modal */}
      <AnimatePresence>
        {showDestroyConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => {
              setShowDestroyConfirmation(false);
              setDestroyConfirmText('');
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface-900 border border-red-500/30 rounded-xl p-6 w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Warning Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-red-400">Destroy All Network Infrastructure</h3>
                  <p className="text-surface-400 text-sm">This action is irreversible</p>
                </div>
              </div>

              {/* Warning Message */}
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                <p className="text-surface-200 text-sm mb-3">
                  This will permanently delete the following resources in <strong className="text-red-400">{networkData?.region || 'your region'}</strong>:
                </p>
                <ul className="text-sm text-surface-300 space-y-1 ml-4">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    {networkData?.vpcs.filter(v => v.tags?.['ManagedBy'] === 'gpu-platform').length || 0} VPC(s) managed by this platform
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    Associated subnets, route tables, and internet gateways
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    Security groups (platform-managed)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    IAM roles and instance profiles
                  </li>
                </ul>
              </div>

              {/* Confirmation Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Type <span className="font-mono text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">destroy</span> to confirm:
                </label>
                <input
                  type="text"
                  value={destroyConfirmText}
                  onChange={(e) => setDestroyConfirmText(e.target.value)}
                  placeholder="Type 'destroy' to confirm"
                  className="input w-full border-red-500/30 focus:border-red-500 focus:ring-red-500/20"
                  autoFocus
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDestroyConfirmation(false);
                    setDestroyConfirmText('');
                  }}
                  className="btn-ghost"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (destroyConfirmText.toLowerCase() === 'destroy') {
                      destroyMutation.mutate();
                      setShowDestroyConfirmation(false);
                      setDestroyConfirmText('');
                    }
                  }}
                  disabled={destroyConfirmText.toLowerCase() !== 'destroy' || isDestroying}
                  className={clsx(
                    'px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all',
                    destroyConfirmText.toLowerCase() === 'destroy'
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-surface-700 text-surface-400 cursor-not-allowed'
                  )}
                >
                  {isDestroying ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Destroying...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Destroy Everything
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
